import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ButtonLoaderComponent } from '../../../../shared/components/button-loader/button-loader.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { CustomEditorComponent } from '../../../../shared/components/custom-editor/custom-editor.component';
import { MultiselectDropdownComponent } from '../../../../shared/components/multiselect-dropdown/multiselect-dropdown.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { TabGroupComponent } from '../../../../shared/components/tab-group/tab-group.component';
import { TabComponent } from '../../../../shared/components/tab/tab.component';
import { UploadAudioComponent } from '../../../../shared/components/upload-audio/upload-audio.component';
import { UploadImageComponent } from '../../../../shared/components/upload-image/upload-image.component';
import {
  AUTHOR_DROPDOWN_CONFIG,
  CATEGORY_DROPDOWN_CONFIG,
} from '../../../../shared/constants/blog.constants';
import {
  ALLOWED_IMAGE_TYPE,
  DEFAULT_FILTER,
} from '../../../../shared/constants/constants';
import {
  FilterModel,
  MediaSignedUrlModel,
} from '../../../../shared/interface/interface';
import { AuthorModel } from '../../../../shared/models/author.model';
import { CategoryModel } from '../../../../shared/models/category.model';
import {
  DropDownConfig,
  DropDownOption,
} from '../../../../shared/models/multiselect-dropdown.model';
import { AuthorService } from '../../../../shared/services/author.service';
import { BlogsService } from '../../../../shared/services/blogs.service';
import { CategoryService } from '../../../../shared/services/category.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SharedModule } from '../../../../shared/shared.module';
import { BlogFaqComponent } from '../blog-faq/blog-faq.component';
import { BlogLinksComponent } from '../blog-links/blog-links.component';
import { BlogSummaryComponent } from '../blog-summary/blog-summary.component';
import { Blog } from '../../../../shared/models/blog.model';
@Component({
  selector: 'app-add-edit-blog',
  standalone: true,
  templateUrl: './add-edit-blog.component.html',
  styleUrl: './add-edit-blog.component.scss',
  imports: [
    SharedModule,
    SkeletonLoaderComponent,
    ButtonLoaderComponent,
    ButtonComponent,
    MultiselectDropdownComponent,
    UploadImageComponent,
    CustomEditorComponent,
    TabGroupComponent,
    TabComponent,
    BlogSummaryComponent,
    UploadAudioComponent,
    BlogFaqComponent,
    BlogLinksComponent,
  ],
})
export class AddEditBlogComponent implements OnInit, OnDestroy {
  authors: AuthorModel[] = [];
  blogForm: FormGroup = this.fb.group({
    title: ['', [Validators.required]],
    description: ['', [Validators.required]],
    img: ['', [Validators.required]],
    content: ['', [Validators.required]],
    category: ['', [Validators.required]],
    author: ['', [Validators.required]],
    publishDateTime: [this.getFormattedDate(new Date()), [Validators.required]],
    snapshot: ['', []],
    blogSummary: [[]],
    audio: ['', []],
    faqTitle: [''],
    blogFaqs: [[]],
    relativeBlogIds: [[], []],
  });

  editor!: any;

  subscription: Subscription = new Subscription();
  blogId: string | null = null;
  allowedImageType = ALLOWED_IMAGE_TYPE.join(',');
  imageUrl: string = '';
  audioUrl: string = '';
  isLoading = false;
  initialLoading = false;
  imageUploading = false;
  audioUploading = false;
  categoryDropdownSettings: DropDownConfig = CATEGORY_DROPDOWN_CONFIG;
  authorDropdownSettings: DropDownConfig = AUTHOR_DROPDOWN_CONFIG;
  categories: CategoryModel[] = [];

  private categoryFilterSubject$: BehaviorSubject<FilterModel> =
    new BehaviorSubject<FilterModel>(DEFAULT_FILTER);

  categoryFilter = this.categoryFilterSubject$.asObservable();
  categoryCount: number = 0;

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private blogsService: BlogsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private categoryService: CategoryService,
    private authorService: AuthorService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  init() {
    const sub = this.activatedRoute.paramMap.subscribe((data: any) => {
      this.blogId = data.get('id');
      this.setInititalBlogValue(this.blogId);
    });
    this.subscribeToFilter();
    this.getAuthorList();
    this.subscription.add(sub);
  }

  onSubmit() {
    if (
      !this.blogForm.valid ||
      this.isLoading ||
      this.initialLoading ||
      this.imageUploading
    ) {
      this.blogForm.markAllAsTouched();
      return;
    }
    const {
      title,
      description,
      img,
      content,
      author,
      audio,
      snapshot,
      blogSummary,
      blogFaqs,
      faqTitle,
      relativeBlogIds
    } = this.blogForm.value;

    const payload = {
      title,
      description,
      img,
      content,
      categoryIds: [this.blogForm.value?.category?.value || ''],
      authorId: author.value,
      publishedAt: new Date(this.blogForm.value.publishDateTime).toISOString(),
      audio: audio,
      snapshot: snapshot,
      summaries: blogSummary.map((summary: any) => {
        if (!summary?.id) {
          delete summary['id'];
        }
        return { ...summary };
      }),
      blogFaqs,
      faqTitle,
      relativeBlogIds: relativeBlogIds?.filter((blog: DropDownOption)=> blog.value !== this.blogId)?.map((blog: DropDownOption)=> blog.value)
    };
    if (this.blogId) {
      this.isLoading = true;
      this.blogsService.updateBlog(this.blogId, payload).subscribe({
        next: (res) => {
          this.toastr.success('Blog updated successfully!');
          this.router.navigate(['/']);
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
        },
      });
    } else {
      this.isLoading = true;
      this.blogsService.addBlog(payload).subscribe({
        next: (res) => {
          this.toastr.success('Blog added successfully!');
          this.router.navigate(['/']);
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
        },
      });
    }
  }

  cancel() {
    this.router.navigate(['/']);
  }

  setInititalBlogValue(blogId: string | null) {
    if (blogId) {
      this.initialLoading = true;

      this.blogsService.getBlog(blogId).subscribe({
        next: (blog: any) => {
          this.initialLoading = false;
          this.blogForm.patchValue({
            title: blog.title,
            description: blog.description,
            img: blog.key,
            content: blog.content,
            category:
              blog.categories.length > 0
                ? {
                    label: blog.categories[0]?.name || '',
                    value: blog.categories[0]?.id || '',
                  }
                : '',
            author: blog.author
              ? { value: blog.author.id, label: blog.author.name }
              : '',
            publishDateTime: this.getFormattedDate(
              blog?.publishedAt ? new Date(blog?.publishedAt) : new Date()
            ),
            snapshot: blog?.snapshot,
            blogSummary: blog?.summaries,
            audio: blog?.audioKey,
            faqTitle: blog?.faqTitle || '',
            blogFaqs: blog?.blogFaqs || [],
            relativeBlogIds: blog?.articleMap?.map((blog: Blog)=>{return { value: blog.id, label: blog.title, isDisabled: blog?.id === this.blogId }}) || []
          });
          this.imageUrl = blog.img;
          this.audioUrl = blog.audio;
        },
        error: (err) => {
          this.router.navigate(['']);
        },
      });
    }
  }

  removeImage() {
    this.blogForm.get('img')?.setValue('');
    this.imageUrl = '';
  }
  getCategoryList(filter: FilterModel) {
    this.isLoading = true;
    this.categoryService.getCategoryList(filter).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.categories =
          filter.pageNumber > 1
            ? [...this.categories, ...res.result]
            : res.result;
        this.categoryCount = res.count;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(
          err?.error?.message || err.message || 'Something went wrong!'
        );
      },
    });
  }

  onImageUploadStart() {
    this.imageUploading = true;
  }
  onImageUploadEnd(data: MediaSignedUrlModel | null) {
    this.imageUploading = false;
    if (data) {
      this.blogForm.get('img')?.setValue(data.key);
      this.imageUrl = data.contentUrl;
    }
  }
  updateCategoryFilter(filter: FilterModel) {
    this.categoryFilterSubject$.next(filter);
  }

  loadMoreIfLastIndex() {
    const { searchText, pageNumber, limit } = this.categoryFilterSubject$.value;
    if (pageNumber * limit < this.categoryCount) {
      this.updateCategoryFilter({
        searchText,
        limit,
        pageNumber: pageNumber + 1,
      });
    }
  }

  subscribeToFilter() {
    const sub = this.categoryFilterSubject$.subscribe((value) => {
      this.getCategoryList(value);
    });
    this.subscription.add(sub);
  }
  handleSearch(searchText: string) {
    if (searchText.length === 0 || searchText?.trim()) {
      this.updateCategoryFilter({
        ...this.categoryFilterSubject$.value,
        pageNumber: 1,
        searchText: searchText,
      });
    }
  }

  getAuthorList() {
    this.isLoading = true;
    this.authorService.fetchAuthors().subscribe({
      next: (res) => {
        this.authors = res.result;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
        this.isLoading = false;
      },
    });
  }

  getFormattedDate(publishDate: Date) {
    const month = (publishDate.getMonth() + 1).toString();
    const date = publishDate.getDate().toString();
    const hrs = publishDate.getHours().toString();
    const mins = publishDate.getMinutes().toString();
    return `${publishDate.getFullYear()}-${
      month.length > 1 ? month : '0' + month
    }-${date.length > 1 ? date : '0' + date}T${
      hrs.length > 1 ? hrs : '0' + hrs
    }:${mins.length > 1 ? mins : '0' + mins}`;
  }

  handleAddBlogSummary(data: any) {
    const summaryArray = this.blogForm.get('blogSummary')?.value;
    this.blogForm
      .get('blogSummary')
      ?.patchValue(summaryArray ? [...summaryArray, data] : [data]);
  }

  removeAudio() {
    this.blogForm.get('audio')?.setValue('');
    this.audioUrl = '';
  }

  onAudioUploadStart() {
    this.audioUploading = true;
  }

  onAudioUploadEnd(data: MediaSignedUrlModel | null) {
    this.audioUploading = false;
    if (data) {
      this.blogForm.get('audio')?.setValue(data.key);
      this.audioUrl = data.contentUrl;
    }
  }
  updateBlogSummary(data: any) {
    if (data.index > -1) {
      const summaryArray = this.blogForm.get('blogSummary')?.value;
      summaryArray[data.index] = data.summary;
      this.blogForm.get('blogSummary')?.patchValue([...summaryArray]);
    }
  }

  deleteSummary(index: number) {
    if (index > -1) {
      const summaryArray = this.blogForm.get('blogSummary')?.value;
      summaryArray?.splice(index, 1);
      this.blogForm.get('blogSummary')?.patchValue([...summaryArray]);
    }
  }

  get categoryOptions(): DropDownOption[] {
    return this.categories.map((category) => ({
      label: category.name,
      value: category.id,
    }));
  }

  get getAuthorOptions(): DropDownOption[] {
    return this.authors.map((author) => ({
      value: author.id,
      label: author.name,
    }));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
